import {Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {MvsCrudService} from "../../../../core/shared/service/crud/mvs-crud.service";
import {
    CampaignScoreStats,
    ExecuteScoringRequest,
    MemberScoreDto,
    ScoreDistributionResponse,
    ScoreHistoryEntry,
    ScoringActionResponse
} from "../../model/dto/api/propensity-scoring.interface";
import {PropensityScoringRunDto} from "../../model/dto/entity/propensity-scoring-run.dto";

/**
 * Service for Propensity Scoring operations (F-029).
 * Provides operations for executing scoring runs and retrieving scores.
 */
@Injectable({
    providedIn: 'root'
})
export class PropensityScoringService {

    private readonly baseUrl: string;

    constructor(private http: HttpClient) {
        this.baseUrl = MvsCrudService.baseUrl + '/ct/propensityScoring';
    }

    /**
     * Executes propensity scoring for a campaign.
     * F-029: Given campaign members, when scoring runs, then each member receives a propensity score.
     *
     * @param campaignId the campaign ID
     * @param modelId the model ID
     */
    executeScoring(campaignId: number, modelId: number): Observable<ScoringActionResponse> {
        const request: ExecuteScoringRequest = {campaignId, modelId};
        return this.http.post<ScoringActionResponse>(`${this.baseUrl}/execute`, request);
    }

    /**
     * Gets all scoring runs for a campaign.
     *
     * @param campaignId the campaign ID
     */
    getRunsForCampaign(campaignId: number): Observable<PropensityScoringRunDto[]> {
        return this.http.get<PropensityScoringRunDto[]>(`${this.baseUrl}/runs/by-campaign/${campaignId}`);
    }

    /**
     * Gets the latest completed scoring run for a campaign.
     *
     * @param campaignId the campaign ID
     */
    getLatestRun(campaignId: number): Observable<PropensityScoringRunDto> {
        return this.http.get<PropensityScoringRunDto>(`${this.baseUrl}/runs/latest/${campaignId}`);
    }

    /**
     * Gets a scoring run by ID.
     *
     * @param runId the run ID
     */
    getRun(runId: number): Observable<PropensityScoringRunDto> {
        return this.http.get<PropensityScoringRunDto>(`${this.baseUrl}/runs/${runId}`);
    }

    /**
     * Cancels a running scoring run.
     *
     * @param runId the run ID
     */
    cancelRun(runId: number): Observable<ScoringActionResponse> {
        return this.http.post<ScoringActionResponse>(`${this.baseUrl}/runs/${runId}/cancel`, {});
    }

    /**
     * Gets score distribution statistics for a scoring run.
     *
     * @param runId the scoring run ID
     */
    getScoreDistribution(runId: number): Observable<ScoreDistributionResponse> {
        return this.http.get<ScoreDistributionResponse>(`${this.baseUrl}/runs/${runId}/distribution`);
    }

    /**
     * Gets campaign members sorted by propensity score.
     * F-029: Given scores are calculated, when agent processes list, then they can sort by score.
     *
     * @param campaignId the campaign ID
     * @param tier optional tier filter (HIGH, MEDIUM, LOW)
     * @param descending whether to sort in descending order
     */
    getMembersSortedByScore(campaignId: number, tier?: string, descending: boolean = true): Observable<MemberScoreDto[]> {
        let url = `${this.baseUrl}/members/${campaignId}?descending=${descending}`;
        if (tier) {
            url += `&tier=${tier}`;
        }
        return this.http.get<MemberScoreDto[]>(url);
    }

    /**
     * Gets score history for a campaign member.
     *
     * @param memberId the campaign member ID
     */
    getScoreHistory(memberId: number): Observable<ScoreHistoryEntry[]> {
        return this.http.get<ScoreHistoryEntry[]>(`${this.baseUrl}/history/${memberId}`);
    }

    /**
     * Gets score statistics for a campaign.
     *
     * @param campaignId the campaign ID
     */
    getCampaignScoreStats(campaignId: number): Observable<CampaignScoreStats> {
        return this.http.get<CampaignScoreStats>(`${this.baseUrl}/stats/${campaignId}`);
    }
}
